import { useEffect, useState } from 'react'
import { X, Sheet, Loader2, ExternalLink, AlertCircle } from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface SheetsExportDialogProps {
  isOpen: boolean
  onClose: () => void
  patternLabel: string
  startDate: string
  rows: Array<{ date: string; status: string }>
}

export default function SheetsExportDialog({
  isOpen,
  onClose,
  patternLabel,
  startDate,
  rows,
}: SheetsExportDialogProps) {
  const [sending, setSending] = useState(false)
  const [sheetUrl, setSheetUrl] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setSending(false)
      setSheetUrl('')
      setError('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const exportRows = rows.slice(0, 400)

  const runExport = async () => {
    setSending(true)
    setError('')
    setSheetUrl('')
    try {
      const result: any = await lumi.functions.invoke('exportToSheets', {
        method: 'POST',
        body: {
          title: `Shift Roster - ${patternLabel} - ${startDate}`,
          patternLabel,
          startDate,
          rows: exportRows,
        },
      })
      if (result?.url) {
        setSheetUrl(result.url)
      } else {
        setError('The spreadsheet was not returned. Please try again.')
      }
    } catch (err: any) {
      console.error('Google Sheets export failed:', err)
      setError(
        err?.data?.error || err?.message || 'Could not export to Google Sheets. Please try again.'
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sheets-dialog-title"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 id="sheets-dialog-title" className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Sheet className="w-5 h-5 text-emerald-600" />
            Export to Google Sheets
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            aria-label="Close Google Sheets dialog"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <p className="text-sm text-slate-600 mb-4">
          Creates a new Google spreadsheet with this roster: one row per day, with the date, weekday and
          shift status for the <span className="font-semibold">{patternLabel}</span> pattern.
        </p>

        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 space-y-1">
          <p>
            <span className="font-semibold">Starts:</span>{' '}
            {new Date(startDate).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          <p>
            <span className="font-semibold">Rows:</span> {exportRows.length}
            {rows.length > 400 ? ' (limited to the first 400 days)' : ''}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {sheetUrl && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
            <p className="font-medium mb-1">Spreadsheet created.</p>
            <a
              href={sheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-green-800 underline hover:text-green-950"
            >
              Open in Google Sheets
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
          >
            {sheetUrl ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={runExport}
            disabled={sending}
            className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? 'Creating...' : sheetUrl ? 'Export again' : 'Create spreadsheet'}
          </button>
        </div>
      </div>
    </div>
  )
}
